<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info header-enhanced">
      <!-- Step Indicator -->
      <b-row class="mb-4">
        <b-col>
          <div class="step-indicator">
            <div class="step-item" :class="{ 'active': step === 1, 'completed': step === 2 }">
              <div class="step-circle">
                <i class="ni ni-image" v-if="step === 1"></i>
                <i class="ni ni-check-bold" v-else></i>
              </div>
              <span class="step-label">Create Album</span>
            </div>
            <div class="step-connector" :class="{ 'active': step === 2 }"></div>
            <div class="step-item" :class="{ 'active': step === 2 }">
              <div class="step-circle">
                <i class="ni ni-music-note"></i>
              </div>
              <span class="step-label">Add Songs</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-music-note mr-2"></i>
            <span v-if="step === 1">Create New Album</span>
            <span v-else>Add Songs to Album</span>
          </h3>
          <p class="text-white-50 mb-0">
            <span v-if="step === 1">Set up your album details and cover image</span>
            <span v-else>Upload and organize your music tracks</span>
          </p>
        </b-col>
        <b-col class="text-right">
          <b-badge :variant="step === 1 ? 'light' : 'success'" class="px-3 py-2">
            <i class="ni ni-compass-04 mr-1"></i>
            Step {{ step }} of 2
          </b-badge>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <div class="d-flex justify-content-center mt-4 containerCreateAlbum">
        <div class="album-container rounded bg-white shadow-lg">
          <!-- Step 1: Create Album -->
          <div class="step-content" v-if="step === 1">
            <div class="row">
              <div class="col-12 col-md-5 col-xl-4">
                <div class="image-upload-section">
                  <h5 class="section-title">
                    <i class="ni ni-image mr-2"></i>Album Cover
                  </h5>
                  <div class="image-container">
                    <croppa v-model="albumCover"
                            accept=".jpeg,.png,.jpg"
                            :width="220"
                            :height="220"
                            :placeholder="placeholder"
                            placeholder-color="#17a2b8"
                            :placeholder-font-size="14"
                            :prevent-white-space="true"
                            canvas-color="#fff"
                            :show-remove-button="true"
                            remove-button-color="#dc3545"
                            :remove-button-size="30"
                            :show-loading="true"
                            :loading-size="30"
                            @file-type-mismatch="onFileTypeMismatch"
                            @new-image-drawn="handleNewImage"
                            @image-remove="handleImageRemove"
                            class="album-croppa">
                    </croppa>
                    <div class="zoom-controls" v-if="albumCover && !isAlbumCoverEmpty">
                      <b-button size="sm" variant="light" class="zoom-btn mr-1"
                               @click="albumCover.zoomIn()"
                               title="Zoom In">
                        <i class="fas fa-search-plus"></i>
                      </b-button>
                      <b-button size="sm" variant="light" class="zoom-btn"
                               @click="albumCover.zoomOut()"
                               title="Zoom Out">
                        <i class="fas fa-search-minus"></i>
                      </b-button>
                    </div>
                  </div>
                  <div class="image-info">
                    <small class="text-muted">
                      <i class="ni ni-info mr-1"></i>
                      Upload a high-quality image (JPEG, PNG) for your album cover
                    </small>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-7 col-xl-8">
                <div class="form-section">
                  <h5 class="section-title">
                    <i class="ni ni-tag mr-2"></i>Album Details
                  </h5>

                  <b-form-group label="Album Name" label-for="albumName" class="mb-4">
                    <b-form-input
                      id="albumName"
                      type="text"
                      placeholder="Enter album name..."
                      aria-describedby="album-name"
                      autocomplete="off"
                      v-model="albumName"
                      @input="$v.albumName.$touch()"
                      :state="!$v.albumName.$error"
                      class="form-control-lg"
                      size="lg">
                    </b-form-input>
                    <b-form-invalid-feedback id="album-name" v-if="$v.albumName.$error">
                      <i class="ni ni-fat-remove mr-1"></i>
                      <span v-if="!$v.albumName.required">Album name is required.</span>
                      <span v-if="!$v.albumName.uniqueAlbumName && $v.albumName.required">
                        This album name is already taken.
                      </span>
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <div class="action-section">
                    <b-button
                      variant="success"
                      size="lg"
                      :disabled="$v.$invalid || isAlbumCoverEmpty"
                      @click="uploadAlbumCover"
                      class="create-btn">
                      <i class="ni ni-fat-add mr-2"></i>
                      Create Album
                    </b-button>
                    <div class="requirements mt-3" v-if="$v.$invalid || isAlbumCoverEmpty">
                      <small class="text-muted">
                        <i class="ni ni-info mr-1"></i>
                        Please provide an album name and cover image to continue.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Step 2: Add Songs -->
          <div class="step-content" v-if="step === 2">
            <div class="row">
              <div class="col-12 col-lg-8">
                <div class="upload-section">
                  <h5 class="section-title">
                    <i class="ni ni-music-note mr-2"></i>Upload Songs
                  </h5>

                  <b-card class="upload-card border-0 shadow-sm">
                    <b-form-group label="Audio File" label-for="audioFile" class="mb-4">
                      <b-form-file
                        id="audioFile"
                        accept="audio/*"
                        aria-describedby="audio-file"
                        placeholder="Choose an MP3 file..."
                        drop-placeholder="Drop your MP3 file here..."
                        ref="audioFileInput"
                        v-model="audioFile"
                        @input="$v.audioFile.$touch()"
                        :state="!$v.audioFile.$error"
                        class="file-input">
                      </b-form-file>
                      <b-form-invalid-feedback id="audio-file" v-if="$v.audioFile.$error">
                        <i class="ni ni-fat-remove mr-1"></i>
                        <span v-if="!$v.audioFile.validSongType">
                          Invalid file type. Please select an MP3 file.
                        </span>
                        <span v-if="$v.audioFile.validSongType && !$v.audioFile.uniqueSelectedSong">
                          This song has already been added to the album.
                        </span>
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <div class="selected-file" v-if="audioFile && !$v.audioFile.$error">
                      <div class="file-info">
                        <i class="ni ni-music-note text-success mr-2"></i>
                        <span class="font-weight-600">{{ audioFile.name }}</span>
                        <small class="text-muted ml-2">
                          ({{ (audioFile.size / (1024 * 1024)).toFixed(2) }} MB)
                        </small>
                      </div>
                    </div>

                    <div class="upload-progress" v-if="uploading">
                      <div class="progress-header mb-2">
                        <span class="font-weight-600">Uploading...</span>
                        <span class="text-muted">{{ progress }}%</span>
                      </div>
                      <b-progress
                        :max="max"
                        :value="progress"
                        variant="success"
                        show-progress
                        animated
                        class="progress-bar-custom">
                      </b-progress>
                    </div>

                    <b-form-group label="Artist Name (Optional)" label-for="artistName" class="mb-4">
                      <b-form-input
                        id="artistName"
                        type="text"
                        placeholder="Enter artist name..."
                        aria-describedby="artist-name"
                        autocomplete="off"
                        v-model="artist"
                        class="artist-input">
                      </b-form-input>
                    </b-form-group>

                    <div class="upload-actions">
                      <b-button
                        :disabled="$v.$invalid"
                        variant="success"
                        size="lg"
                        @click="uploadSong"
                        class="upload-btn mr-3">
                        <i class="ni ni-cloud-upload-96 mr-2"></i>
                        Upload Song
                      </b-button>
                      <b-button
                        variant="outline-primary"
                        size="lg"
                        @click="done"
                        class="done-btn">
                        <i class="ni ni-check-bold mr-2"></i>
                        <span v-if="albumSongs && albumSongs.length > 0">Done</span>
                        <span v-else>Skip for Now</span>
                      </b-button>
                    </div>

                    <div class="upload-info mt-3">
                      <small class="text-muted">
                        <i class="ni ni-info mr-1"></i>
                        You can upload multiple songs one by one. Supported format: MP3 only.
                      </small>
                    </div>
                  </b-card>
                </div>
              </div>

              <div class="col-12 col-lg-4">
                <div class="songs-preview">
                  <h5 class="section-title">
                    <i class="ni ni-bullet-list-67 mr-2"></i>Album Songs
                  </h5>
                  <SongsList :songs="albumSongs" class="songs-list-enhanced"></SongsList>
                  <div v-if="!albumSongs || albumSongs.length === 0" class="empty-songs">
                    <div class="empty-state">
                      <i class="ni ni-music-note text-muted"></i>
                      <p class="text-muted mb-0 mt-2">No songs uploaded yet</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <loading :active.sync="isLoading"
             :is-full-page="true"
             background-color="#000"
             color="#068bcd"
             :opacity="0.5"
             :width="70"
             :height="70"
    ></loading>
    <v-dialog/>
  </div>
</template>
<script>
  const Loading = () => import('vue-loading-overlay');
  const SongsList = () => import('./SongList.vue');
  import { mapGetters, mapActions } from 'vuex';
  import { required } from 'vuelidate/lib/validators';
  import 'vue-croppa/dist/vue-croppa.css';
  import 'vue-loading-overlay/dist/vue-loading.css';
  import baseHeader from '../../BaseHeader';

  export default {
    name     : 'CreateAlbum',
    computed : {
      ...mapGetters({
        albumSongs : 'media/albumSongs',
      }),
    },
    data() {
      return {
        isLoading          : false,
        fullPage           : true,
        step               : 1,
        audioFile          : null,
        albumCover         : null,
        uploading          : false,
        progress           : 0,
        albumName          : null,
        uploaded           : false,
        max                : 100,
        albumId            : null,
        musicId            : null,
        uniqueAlbumName    : true,
        uniqueSelectedSong : true,
        validSongType      : true,
        placeholder        : 'Album Cover',
        isAlbumCoverEmpty  : true,
        artist             : null,
      };
    },
    watch : {
      albumName(value) {
        this.uniqueAlbumName = true;
        if (value)
          this.checkAlbumName();
      },

      audioFile(value) {
        this.validSongType = true;
        if (value) {
          if (value.type === 'audio/mp3' || value.type === 'audio/mpeg')
            this.checkSongName();
          else
            this.validSongType = false;
        }
      },
    },
    methods : {
      ...mapActions({
        'getAlbumSongs'   : 'media/getAlbumSongs',
        'clearAlbumSongs' : 'media/clearAlbumSongs',
      }),

      /**
       * Check if Album Name is unique
       */
      // eslint-disable-next-line no-undef
      checkAlbumName : _.debounce(function() {
        this.$http.get('/mobile/api/album/albumname', {
          params : {
            albumName : this.albumName,
          },
        }).then(response => {
          this.uniqueAlbumName = (response.data) ? false : true;
        });
      }, 500),

      /**
       * Check if Song Name is unique
       */
      // eslint-disable-next-line no-undef
      checkSongName : _.debounce(function() {
        // eslint-disable-next-line no-undef
        const filename = _.split(this.audioFile.name, '.');
        this.$http.get('/mobile/api/album/songname', {
          params : {
            songName : filename[0],
            albumId  : this.albumId,
          },
        }).then(response => {
          this.uniqueSelectedSong = (response.data) ? false : true;
        });
      }, 500),


      /**
       * Upload Album Cover
       */
      async uploadAlbumCover() {
        this.albumCover.generateBlob(async (blob) => {
          const formData = new FormData();
          formData.append('file', blob, 'image.jpg');
          formData.append('imageType', 'album_cover');
          formData.append('albumName', this.albumName);
          formData.append('action', 'Add');

          try {
            this.isLoading = true;
            const result = await
              this.$http.post('/api/upload/image/album/thumbnail', formData, {
                onUploadProgress : e =>
                  this.progress = Math.round(e.loaded * 100 / e.total),
              }
              );

            if (result) {
              this.isLoading = false;
              this.albumId = result.data.albumId;
              this.clearAlbumSongs();
              this.step = 2; // Go to next step, w/c is Add Song
              this.$notify({
                group : 'notif',
                type  : 'success',
                title : 'Success!',
                text  : 'Created Album!',
              });
            }
          } catch (error) {
            this.isLoading = false;
            this.$notify({
              group : 'notif',
              type  : 'failed',
              title : 'Failed',
              text  : 'Oops! Something went wrong!',
            });
          }
        }, 'image/jpeg');
      },

      /**
       * Close Modal
       */
      onModalClose() {

      },

      /**
       * Done uploading songs
       */
      done() {
        this.clearAlbumSongs();
        this.step = 1;
        this.albumId = null;
        this.musicId = null;
        this.albumName = null;
      },

      /**
       * Upload Song
       */
      async uploadSong() {
        if (this.audioFile) {
          const formData = new FormData();
          formData.append('file', this.audioFile);
          formData.append('albumName', this.albumName);
          formData.append('albumId', this.albumId);
          formData.append('notify', false);
          if(this.artist)
            formData.append('artist', this.artist);

          try {
            this.isLoading = true;
            this.uploading = true;
            const result = await this.$http.post('/api/add/mp3', formData, {
              onUploadProgress : e =>
                this.progress = Math.round(e.loaded * 100 / e.total),
            });

            if (result) {
              this.uploading = false;
              this.uploaded = true;
              this.progress = 0;
              this.artist = null;
              this.getAlbumSongs(this.albumId);
              const filename = _.split(this.audioFile.name, '.');
              this.$notify({
                group : 'notif',
                type  : 'success',
                title : 'Success!',
                text  : 'Added ' + filename[0] + ' successfully!',
              });
              this.audioFile = null;
              this.$nextTick(() => {
                this.$v.$reset();
              });
              this.isLoading = false;
            }
          } catch (err) {
            this.isLoading = false;
            this.$notify({
              group : 'notif',
              type  : 'failed',
              title : 'Failed',
              text  : 'Oops! Something went wrong!',
            });
          }
        }
      },

      /**
       * Check if File type is valid
       */
      onFileTypeMismatch() {
        this.placeholder = 'Invalid file type.';
      },

      /**
       * Callback function after croppa image selection
       */
      handleNewImage() {
        this.isAlbumCoverEmpty = false;
      },

      /**
       * Callback function after croppa image removed
       */
      handleImageRemove() {
        this.isAlbumCoverEmpty = true;
      },
    },
    components : {
      SongsList,
      Loading,
      baseHeader
    },
    validations() {
      if (this.step === 1) {
        return {
          albumName : {
            required,
            uniqueAlbumName() {
              return this.uniqueAlbumName;
            },
          },
          albumCover : { required },
        }
      }
      if (this.step === 2) {
        return {
          audioFile : {
            required,
            uniqueSelectedSong() {
              return this.uniqueSelectedSong;
            },
            validSongType() {
              return this.validSongType;
            },
          },
        }
      }
    },
  };
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/mobile_music/create-album";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9) 0%, rgba(0, 123, 191, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(0, 123, 191, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(0, 123, 191, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Step Indicator */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 3px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  transition: all 0.3s ease;
  margin-bottom: 0.5rem;
}

.step-item.active .step-circle {
  background: #fff;
  border-color: #fff;
  color: #17a2b8;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.step-item.completed .step-circle {
  background: #28a745;
  border-color: #28a745;
  color: #fff;
}

.step-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
}

.step-item.active .step-label {
  color: #fff;
  font-weight: 600;
}

.step-connector {
  width: 100px;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 1rem;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.step-connector.active {
  background: #fff;
}

/* Album Container */
.album-container {
  max-width: 1200px;
  width: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
  border: 1px solid rgba(23, 162, 184, 0.1);
  animation: fadeInUp 0.8s ease-out;
}

.step-content {
  padding: 2rem;
}

/* Image Upload Section */
.image-upload-section {
  text-align: center;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(248, 249, 250, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%);
  border-radius: 12px;
  margin-bottom: 2rem;
}

.section-title {
  color: #344767;
  font-weight: 600;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.image-container {
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
}

.album-croppa {
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(23, 162, 184, 0.2);
  transition: all 0.3s ease;
}

.album-croppa:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  border-color: rgba(23, 162, 184, 0.4);
}

.zoom-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  gap: 0.5rem;
}

.zoom-btn {
  border-radius: 50%;
  width: 35px;
  height: 35px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.zoom-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.image-info {
  margin-top: 1rem;
}

/* Form Section */
.form-section {
  padding: 2rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(23, 162, 184, 0.1);
}

.form-control-lg {
  border-radius: 8px;
  border: 2px solid rgba(23, 162, 184, 0.2);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control-lg:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.action-section {
  text-align: center;
  margin-top: 2rem;
}

.create-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.requirements {
  margin-top: 1rem;
}

/* Upload Section */
.upload-section {
  margin-bottom: 2rem;
}

.upload-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 100%);
  border-radius: 12px;
}

.file-input {
  border: 2px dashed rgba(23, 162, 184, 0.3);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: rgba(23, 162, 184, 0.02);
}

.file-input:hover {
  border-color: rgba(23, 162, 184, 0.6);
  background: rgba(23, 162, 184, 0.05);
}

.selected-file {
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
}

.file-info {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.upload-progress {
  margin: 1rem 0;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.progress-bar-custom {
  height: 8px;
  border-radius: 4px;
}

.artist-input {
  border-radius: 8px;
  border: 2px solid rgba(23, 162, 184, 0.2);
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
}

.artist-input:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.upload-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.upload-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.done-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.done-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

.upload-info {
  text-align: center;
}

/* Songs Preview */
.songs-preview {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(23, 162, 184, 0.1);
  height: fit-content;
}

.songs-list-enhanced {
  max-height: 400px;
  overflow-y: auto;
}

.empty-songs {
  text-align: center;
  padding: 2rem;
}

.empty-state {
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-indicator {
    flex-direction: column;
    gap: 1rem;
  }

  .step-connector {
    width: 3px;
    height: 40px;
    margin: 0.5rem 0;
  }

  .step-content {
    padding: 1rem;
  }

  .image-upload-section,
  .form-section {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .upload-actions {
    flex-direction: column;
    align-items: center;
  }

  .upload-btn,
  .done-btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .songs-preview {
    margin-top: 2rem;
  }
}

/* Legacy styles */
table, .containerCreateAlbum {
  z-index: 99;
  position: relative;
}
</style>
